import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Grid,
  Paper,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import TeamLogo from '../../../components/team/TeamLogo';

const LeagueDisciplinaryReport = () => {
  const { leagueId } = useParams();
  const [reportData, setReportData] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderBy, setOrderBy] = useState('gameDate');
  const [order, setOrder] = useState('desc');

  const fetchReportData = useCallback(async () => {
    if (!leagueId) return;

    setLoading(true);
    setError(null);

    try {
      const [disciplinaryData, teamsData] = await Promise.all([
        unifiedApi.getLeagueDisciplinaryReport(leagueId),
        unifiedApi.getLeagueTeamsWithManagers(leagueId)
      ]);
      
      setReportData(disciplinaryData);
      setTeams(teamsData || []);
    } catch (err) {
      console.error('Failed to fetch disciplinary report:', err);
      setError(err.message || 'Failed to load disciplinary report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getCardColor = (cardType) => {
    switch (cardType) {
      case 'YellowCard':
        return 'warning';
      case 'RedCard':
        return 'error';
      case 'SecondYellowRed':
        return 'error';
      default:
        return 'default';
    }
  };

  // Calculate team stats from incidents and teams data
  const teamStats = React.useMemo(() => {
    if (!teams || !reportData?.incidents) return [];

    // Normalize team name for consistent matching
    const normalizeTeamName = (name) => name?.trim().toLowerCase() || '';

    // Create a map of normalized team name to stats from incidents
    const statsMapByName = {};
    
    reportData.incidents.forEach(incident => {
      const teamName = incident.playerTeamName;
      const normalizedKey = normalizeTeamName(teamName);
      
      // Store by normalized name
      if (!statsMapByName[normalizedKey]) {
        statsMapByName[normalizedKey] = {
          teamName,
          totalCards: 0,
          yellowCards: 0,
          redCards: 0,
          secondYellowReds: 0,
          regularYellowCards: 0,
          regularRedCards: 0,
          regularSecondYellowReds: 0,
          spareYellowCards: 0,
          spareRedCards: 0,
          spareSecondYellowReds: 0
        };
      }

      const stats = statsMapByName[normalizedKey];
      stats.totalCards++;

      if (incident.cardType === 'YellowCard') {
        stats.yellowCards++;
        if (incident.isSpare) stats.spareYellowCards++;
        else stats.regularYellowCards++;
      } else if (incident.cardType === 'RedCard') {
        stats.redCards++;
        if (incident.isSpare) stats.spareRedCards++;
        else stats.regularRedCards++;
      } else if (incident.cardType === 'SecondYellowRed') {
        stats.secondYellowReds++;
        if (incident.isSpare) stats.spareSecondYellowReds++;
        else stats.regularSecondYellowReds++;
      }
    });

    // Merge with teams data to get logo and include teams with no cards
    const result = teams.map(team => {
      // Look up stats using normalized team name
      const normalizedKey = normalizeTeamName(team.name);
      const matchingStats = statsMapByName[normalizedKey];

      return {
        teamId: team.id,
        teamName: team.name,
        logoUrl: team.logoUrl,
        managerName: team.manager ? team.manager.fullName : 'No Manager',
        totalCards: matchingStats?.totalCards || 0,
        yellowCards: matchingStats?.yellowCards || 0,
        redCards: matchingStats?.redCards || 0,
        secondYellowReds: matchingStats?.secondYellowReds || 0,
        regularYellowCards: matchingStats?.regularYellowCards || 0,
        regularRedCards: matchingStats?.regularRedCards || 0,
        regularSecondYellowReds: matchingStats?.regularSecondYellowReds || 0,
        spareYellowCards: matchingStats?.spareYellowCards || 0,
        spareRedCards: matchingStats?.spareRedCards || 0,
        spareSecondYellowReds: matchingStats?.spareSecondYellowReds || 0
      };
    });
    
    return result.sort((a, b) => b.totalCards - a.totalCards); // Sort by total cards descending
  }, [teams, reportData]);

  // Calculate optimal grid size for even distribution across rows
  const gridSize = React.useMemo(() => {
    const teamCount = teamStats.length;
    if (teamCount === 0) return { xs: 12, sm: 6, md: 4, lg: 2.4 };
    
    // Calculate optimal teams per row to get ~2 rows with balanced distribution
    let teamsPerRow;
    if (teamCount <= 2) teamsPerRow = teamCount;
    else if (teamCount <= 6) teamsPerRow = 3;
    else if (teamCount <= 8) teamsPerRow = 4;
    else teamsPerRow = 5;
    
    // Convert to grid size (12-column system)
    const lgSize = 12 / teamsPerRow;
    
    return {
      xs: 12,
      sm: 6,
      md: teamCount <= 6 ? 4 : 3,
      lg: lgSize
    };
  }, [teamStats.length]);

  const sortedIncidents = reportData?.incidents?.slice().sort((a, b) => {
    let comparison = 0;
    
    switch (orderBy) {
      case 'gameDate':
        comparison = new Date(b.gameDate) - new Date(a.gameDate);
        break;
      case 'playerName':
        comparison = a.playerName.localeCompare(b.playerName);
        break;
      case 'teamName':
        comparison = a.playerTeamName.localeCompare(b.playerTeamName);
        break;
      case 'cardType':
        comparison = a.cardType - b.cardType;
        break;
      case 'gameWeek':
        comparison = a.gameWeek - b.gameWeek;
        break;
      default:
        comparison = 0;
    }
    
    return order === 'asc' ? comparison : -comparison;
  }) || [];

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          component={Link}
          to={`/admin/leagues/${leagueId}`}
          startIcon={<ArrowBackIcon />}
        >
          Back to League Dashboard
        </Button>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <Box sx={{ mb: { xs: 2, sm: 4 } }}>
        <Button
          component={Link}
          to={`/admin/leagues/${leagueId}`}
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 2 }}
          size="small"
        >
          Back to League Dashboard
        </Button>
        
        <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Disciplinary Report (Cards)
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          {reportData?.leagueName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Generated on {new Date(reportData?.generatedAt).toLocaleDateString()}
        </Typography>
      </Box>

      {/* Summary Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main">
                {reportData?.summary?.totalYellowCards || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Yellow Cards
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main">
                {reportData?.summary?.totalRedCards || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Red Cards
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.dark">
                {reportData?.summary?.totalSecondYellowReds || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Second Yellow → Red
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main">
                {reportData?.summary?.gamesWithCards || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Games with Cards
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Team Statistics */}
      {teamStats && teamStats.length > 0 && (
        <Paper sx={{ mb: 4, p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Cards by Team
          </Typography>
          <Grid container spacing={2}>
            {teamStats.map((team) => (
              <Grid key={team.teamId} size={gridSize}>
                <Card variant="outlined">
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <TeamLogo 
                        team={{ logoUrl: team.logoUrl, name: team.teamName }}
                        size={40}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontSize: '1rem', lineHeight: 1.2 }} noWrap>
                          {team.teamName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }} noWrap>
                          {team.managerName}
                        </Typography>
                      </Box>
                      <Typography variant="h5" color={team.totalCards > 0 ? 'error.main' : 'text.secondary'} sx={{ fontSize: '1.75rem' }}>
                        {team.totalCards}
                      </Typography>
                    </Box>
                    
                    {team.totalCards > 0 ? (
                      <>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold', display: 'block' }}>
                            Card Breakdown
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            {team.yellowCards > 0 && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2">Yellow:</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{team.yellowCards}</Typography>
                              </Box>
                            )}
                            {team.secondYellowReds > 0 && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2">2nd Yellow → Red:</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{team.secondYellowReds}</Typography>
                              </Box>
                            )}
                            {team.redCards > 0 && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2">Red:</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{team.redCards}</Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {(team.spareYellowCards > 0 || team.spareRedCards > 0 || team.spareSecondYellowReds > 0) && (
                          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" color="info.main" sx={{ fontWeight: 'bold' }}>
                                Cards by Spares:
                              </Typography>
                              <Typography variant="body2" color="info.main" sx={{ fontWeight: 'bold' }}>
                                {team.spareYellowCards + team.spareRedCards + team.spareSecondYellowReds}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </>
                    ) : (
                      <Alert severity="success" sx={{ mt: 1 }}>
                        No cards - Excellent sportsmanship!
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Incidents Table */}
      <Paper sx={{ mb: 4 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            All Disciplinary Incidents ({sortedIncidents.length})
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'gameWeek'}
                    direction={orderBy === 'gameWeek' ? order : 'asc'}
                    onClick={() => handleRequestSort('gameWeek')}
                  >
                    Week
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'gameDate'}
                    direction={orderBy === 'gameDate' ? order : 'asc'}
                    onClick={() => handleRequestSort('gameDate')}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>Game</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'playerName'}
                    direction={orderBy === 'playerName' ? order : 'asc'}
                    onClick={() => handleRequestSort('playerName')}
                  >
                    Player
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'teamName'}
                    direction={orderBy === 'teamName' ? order : 'asc'}
                    onClick={() => handleRequestSort('teamName')}
                  >
                    Team
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'cardType'}
                    direction={orderBy === 'cardType' ? order : 'asc'}
                    onClick={() => handleRequestSort('cardType')}
                  >
                    Card
                  </TableSortLabel>
                </TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedIncidents.length > 0 ? (
                sortedIncidents.map((incident) => (
                  <TableRow key={incident.id} hover>
                    <TableCell>{incident.gameWeek}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {new Date(incident.gameDate).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {incident.homeTeamName} vs {incident.awayTeamName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                        {incident.finalScore}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                          {incident.playerName}
                        </Typography>
                        {incident.isSpare && (
                          <Chip 
                            label="Spare" 
                            size="small" 
                            color="info" 
                            variant="outlined"
                            onClick={() => {/* do nothing */}}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {incident.playerTeamName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={incident.cardTypeName} 
                        size="small" 
                        color={getCardColor(incident.cardType)}
                        onClick={() => {/* do nothing */}}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 500 }}>
                        {incident.notes || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                      No disciplinary incidents recorded - excellent sportsmanship!
                    </Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </AdminLayout>
  );
};

export default LeagueDisciplinaryReport;
